"use client";

import { useState, useMemo } from "react";
import { createBrowserClient } from "@supabase/ssr";
import { Button } from "@/components/ui/button";
import { ShieldCheck, UserCircle, Eye } from "lucide-react";

const DEMO_ACCOUNTS = [
  { label: "Admin Demo", email: "admin@demo.fluxity.ai", password: "demo-admin-2026", icon: ShieldCheck },
  { label: "Member Demo", email: "member@demo.fluxity.ai", password: "demo-member-2026", icon: UserCircle },
  { label: "Viewer Demo", email: "viewer@demo.fluxity.ai", password: "demo-viewer-2026", icon: Eye },
] as const;

export function SandboxDemoLogin() {
  const [loading, setLoading] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const supabase = useMemo(
    () =>
      createBrowserClient(
        process.env.NEXT_PUBLIC_SUPABASE_URL || "https://placeholder.supabase.co",
        process.env.NEXT_PUBLIC_SUPABASE_ANON_KEY || "placeholder"
      ),
    []
  );

  const handleDemoLogin = async (email: string, password: string) => {
    setLoading(email);
    setError(null);
    try {
      const { error } = await supabase.auth.signInWithPassword({ email, password });
      if (error) throw error;
      window.location.href = "/dashboard";
    } catch (err) {
      setError(err instanceof Error ? err.message : "Demo login failed");
      setLoading(null);
    }
  };

  if (process.env.NEXT_PUBLIC_FLUXITY_ENV !== "sandbox") return null;

  return (
    <div className="mt-6 pt-6 border-t border-border">
      <p className="text-xs text-muted-foreground text-center mb-3 uppercase tracking-wider">
        Quick Demo Login
      </p>
      <div className="space-y-2">
        {DEMO_ACCOUNTS.map((account) => (
          <Button
            key={account.email}
            type="button"
            variant="outline"
            fullWidth
            className="h-9 text-xs"
            loading={loading === account.email}
            disabled={loading !== null}
            onClick={() => handleDemoLogin(account.email, account.password)}
          >
            <account.icon className="size-3.5 mr-2" />
            {account.label}
          </Button>
        ))}
      </div>
      {error && (
        <p className="text-xs text-destructive mt-2 text-center">{error}</p>
      )}
    </div>
  );
}
